import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resourceToUri } from 'jwt-cert-sdk';

import { useAppSelector, useAppDispatch } from '../store/hook';
import {
  pollPayment,
  clearPayment,
  fetchPaymentMethods,
  createPaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  processPaymentWithExistingPaymentMethod,
  processPaymentWithSpreedlyToken,
  createSpreedlyCreditCard,
} from '../store/payments/paymentsReducer';

import { useData } from './ContentProvider';
import { CardInfo } from './model';
import { useEventServiceIdentity } from './api/EventApi';

import {
  selectPaymentMethodsArray,
  selectPaymentStatus,
  selectStatus,
} from 'store/payments/paymentsSelectors';
import { selectCurrentWallet } from 'store/orders/ordersSelectors';

const usePayments = () => {
  const [error, _setError] = useState<unknown | any>();
  const { securedApi } = useData();
  const paymentMethods = useAppSelector(selectPaymentMethodsArray);
  const currentWallet: any = useAppSelector(selectCurrentWallet);
  const paymentStatus: any = useAppSelector(selectPaymentStatus);
  const status: any = useAppSelector(selectStatus);
  const { eventId } = useParams();
  const eventServiceIdentity = useEventServiceIdentity();
  const eventUri =
    eventServiceIdentity && eventId
      ? resourceToUri({
          resourceId: eventId,
          serviceId: eventServiceIdentity?.sub,
          resourceType: 'cb:event',
        })
      : null;
  const [isSuccess, _setSuccess] = useState<boolean>(); // TODO: modify
  const [isLoading, _setLoading] = useState<boolean>(false); // TODO: modify
  const dispatch = useAppDispatch();
  const assignedAccountId = currentWallet?.wallet?.accountId;

  const clearPaymentStatus = useCallback(() => {
    dispatch(clearPayment());
  }, [dispatch]);

  const getAllPaymentMethods = useCallback(async () => {
    if (!eventUri || !assignedAccountId) return;
    return dispatch(fetchPaymentMethods({ securedApi, eventUri, accountId: assignedAccountId }));
  }, [assignedAccountId, dispatch, eventUri, securedApi]);

  useEffect(() => {
    if (!paymentMethods && securedApi && eventUri && assignedAccountId) {
      getAllPaymentMethods();
    }
  }, [securedApi, getAllPaymentMethods, paymentMethods, eventUri, assignedAccountId]);

  useEffect(() => {
    _setLoading(status === 'loading');
  }, [status]);

  const changeDefaultPayment = useCallback(
    async (uuid: string, eventUri: any) => {
      return dispatch(
        setDefaultPaymentMethod({ uuid, securedApi, eventUri, accountId: assignedAccountId }),
      );
    },
    [assignedAccountId, dispatch, securedApi],
  );

  const addPaymentMethod = useCallback(
    async (cardInfo: CardInfo, eventUri: any, accountIdOverride?: string) => {
      const accountId = accountIdOverride || assignedAccountId;
      if (!accountId) return;
      const isFirstPaymentMethod = paymentMethods?.length === 0;

      return dispatch(
        createPaymentMethod({
          cardInfo: {
            ...cardInfo,
            default: isFirstPaymentMethod || cardInfo.default,
          },
          securedApi,
          eventUri,
          accountId,
        }),
      );
    },
    [assignedAccountId, dispatch, paymentMethods, securedApi],
  );

  const deletePaymentMethodByUuid = useCallback(
    async (uuid: string, eventUri: any) => {
      const isDefault = paymentMethods.find((method) => method.uuid === uuid)?.default;
      const replacements = paymentMethods.filter((method) => method.uuid !== uuid);
      const replacementId = isDefault && !!replacements.length ? replacements[0].uuid : null;
      replacementId ? changeDefaultPayment(replacementId, eventUri) : null;
      return dispatch(
        deletePaymentMethod({ uuid, securedApi, eventUri, accountId: assignedAccountId }),
      );
    },
    [assignedAccountId, changeDefaultPayment, dispatch, paymentMethods, securedApi],
  );

  const processWithSavedPaymentMethod = useCallback(
    async (paymentId: string, paymentMethodId: string) => {
      return dispatch(
        processPaymentWithExistingPaymentMethod({ paymentId, paymentMethodId, securedApi }),
      );
    },
    [dispatch, securedApi],
  );

  const pollPaymentStatus = useCallback(
    async (paymentId: string) => {
      try {
        return await dispatch(pollPayment({ paymentId, dispatch, securedApi })).unwrap();
      } catch (error) {
        console.error('Error in pollPaymentStatus:', error);
        throw error;
      }
    },
    [dispatch, securedApi],
  );

  const tokenizeCreditCard = useCallback(
    async (cardInfo: CardInfo) => {
      return dispatch(createSpreedlyCreditCard({ cardInfo, securedApi }));
    },
    [dispatch, securedApi],
  );

  const processWithNewPaymentMethod = useCallback(
    async (paymentId: string, spreedlyToken: string) => {
      return dispatch(processPaymentWithSpreedlyToken({ paymentId, spreedlyToken, securedApi }));
    },
    [dispatch, securedApi],
  );

  return {
    error,
    status,
    paymentMethods,
    isSuccess,
    isLoading,
    paymentStatus,
    clearPaymentStatus,
    pollPaymentStatus,
    getAllPaymentMethods,
    addPaymentMethod,
    deletePaymentMethodByUuid,
    changeDefaultPayment,
    processWithSavedPaymentMethod,
    processWithNewPaymentMethod,
    tokenizeCreditCard,
  };
};
export default usePayments;
